import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { HubFramework } from '../../HubFramework'
import { HubPathRouting } from '../../HubFramework/pathBuilder'
import { useMain } from '../../Utils/SessionControls/mainContext'
import { GlobalDictionary } from '../../Utils/globalDictionary'
import { moduleContent } from './moduleContent'
import { getRepos, getSecrets, getTeams } from './BackendFacade/apiCalls'
import { GithubDataContext } from './githubDataContext'
import { getProject } from '../ProjectContent/BackendFacade/apiCalls'
import { IRepo, ISecret, ITeam } from './Interfaces/IGithub'

type ModuleState = {
  error?: string
  repos: IRepo[]
  secrets: ISecret[]
  teams: ITeam[]
  costCenter: string
  loading: boolean
}

export type GithubModuleContext = {
  state: ModuleState
  refreshData: () => Promise<void>
}

const initialState: ModuleState = {
  error: undefined,
  repos: [],
  secrets: [],
  teams: [],
  costCenter: '',
  loading: true
}

export const Module: React.FC = () => {
  const { setLoading } = useMain()

  const hasInitializedRouting = React.useRef(false)

  if (!hasInitializedRouting.current) {
    HubPathRouting.setProject()
    HubPathRouting.setModule()
    hasInitializedRouting.current = true
  }

  const cachedProject = GlobalDictionary.get(
    GlobalDictionary.PROJECTINFO + HubPathRouting.currentProject.id
  )

  const [state, setState] = useState<ModuleState>({
    ...initialState,
    costCenter: cachedProject?.costCenter ?? ''
  })

  const [linkedId, setLinkedId] = useState(
    HubPathRouting.currentModule?.moduleLinkedId ?? ''
  )

  const getCostCenter = useCallback(async (): Promise<void> => {
    if (
      !GlobalDictionary.get(
        GlobalDictionary.PROJECTINFO + HubPathRouting.currentProject.id
      )
    ) {
      try {
        const data = await getProject(HubPathRouting.currentProject.id)
        if (data.success) {
          const project = data.data.value
          GlobalDictionary.set(
            GlobalDictionary.PROJECTINFO + HubPathRouting.currentProject.id,
            project
          )
          setState(prev => ({
            ...prev,
            costCenter: project.costCenter
          }))
        } else {
          setState(prev => ({
            ...prev,
            error: data.data.errorMessage || 'Failed to fetch project information.'
          }))
        }
      } catch (e) {
        setState(prev => ({
          ...prev,
          error: e instanceof Error ? e.message : String(e)
        }))
      }
    }
  }, [])

  const fetchAll = useCallback(async (): Promise<void> => {
    setState(prev => ({
      ...prev,
      loading: true
    }))
    try {
      const repoPromise = getRepos()
      const secretPromise = getSecrets()
      const teamPromise = getTeams()

      const repoData = await repoPromise
      const secretData = await secretPromise
      const teamData = await teamPromise

      setState(prev => ({
        ...prev,
        repos: repoData,
        secrets: secretData,
        teams: teamData,
        error: undefined,
        loading: false
      }))
    } catch (e) {
      setState(prev => ({
        ...prev,
        error: e instanceof Error ? e.message : String(e),
        loading: false
      }))
    }
  }, [])

  const loadEverything = useCallback(async () => {
    setLoading(true)
    await getCostCenter()
    await fetchAll()
    setLoading(false)
  }, [fetchAll, getCostCenter, setLoading])

  useEffect(() => {
    const handleRoutingChange = () => {
      setLinkedId(HubPathRouting.currentModule?.moduleLinkedId ?? '')
    }

    HubPathRouting.subscribe(handleRoutingChange)
    handleRoutingChange()

    return () => {
      HubPathRouting.unsubscribe(handleRoutingChange)
    }
  }, [])

  useEffect(() => {
    loadEverything()
  }, [loadEverything])

  const moduleContext = useMemo<GithubModuleContext>(
    () => ({
      state,
      refreshData: fetchAll
    }),
    [state, fetchAll]
  )

  const providerValue = useMemo(
    () => ({
      repos: state.repos,
      secrets: state.secrets,
      teams: state.teams,
      refreshData: () => {
        void fetchAll()
      },
      org: linkedId?.split('/')[0] || '',
      slug: linkedId?.split('/')[1]?.toUpperCase() || '',
      costCenter: state.costCenter,
      loading: state.loading
    }),
    [state, fetchAll, linkedId]
  )

  return (
    <GithubDataContext.Provider value={providerValue}>
      <HubFramework hubContent={moduleContent} module={moduleContext} />
    </GithubDataContext.Provider>
  )
}
